import { type HTMLAttributes, type ReactNode, forwardRef } from 'react'

import { cn } from '@/lib/ui/style-helpers'

import { Button, type ButtonProps } from '@/components/lemmala-design-system/button/button'

import { subtitleVariants } from './variants'

import NotFoundIcon from '@/assets/404.svg'
import Empty1Icon from '@/assets/empty1.svg'
import Empty2Icon from '@/assets/empty2.svg'
import EmptyChartIcon from '@/assets/empty-chart.svg'

const VARIANT_ICON_MAP = {
  primary: Empty1Icon,
  secondary: Empty2Icon,
  'not-found': NotFoundIcon,
  chart: EmptyChartIcon,
} as const

type EmptyProps = Omit<HTMLAttributes<HTMLDivElement>, 'title'> & {
  icon?: keyof typeof VARIANT_ICON_MAP
  title?: string
  subtitle?: ReactNode
  button?: Omit<ButtonProps, 'children'> & {
    label: ReactNode
  }
  variant?: 'default' | 'card'
  subtitleWidth?: 'constrained' | 'full'
}

export const Empty = forwardRef<HTMLDivElement, EmptyProps>(
  (
    { className, icon, title, subtitle, button, variant = 'default', subtitleWidth, ...props },
    ref,
  ) => {
    const IconComponent = icon ? VARIANT_ICON_MAP[icon] : null

    const { label: buttonLabel, ...buttonProps } = button ?? {}

    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-col items-center justify-center gap-3 rounded-lg px-8 pb-8 pt-4',
          variant === 'card' && 'h-full rounded border bg-main-background-card py-14',
          className,
        )}
        {...props}
      >
        {IconComponent && (
          <div className='flex justify-center'>
            <IconComponent className='size-32' />
          </div>
        )}

        {(title || subtitle) && (
          <div className='flex flex-col items-center gap-1'>
            {title && (
              <h3 className='max-w-sm text-center text-main-foreground-primary text-heading-xsmall'>
                {title}
              </h3>
            )}

            {subtitle && <p className={subtitleVariants({ subtitleWidth })}>{subtitle}</p>}
          </div>
        )}

        {button && (
          <Button variant={'outline'} {...buttonProps}>
            {buttonLabel}
          </Button>
        )}
      </div>
    )
  },
)

Empty.displayName = 'Empty'
